import L, { type LayerGroup, type LeafletMouseEvent, type Map as LeafletMap, type Marker, type TileLayer } from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  APIError,
  getDevices,
  getMapLineCatalog,
  replaceMapLineCatalog,
  type Device,
  type MapLineCatalog,
  type MapLineData,
  type MapMovementData,
  type MapSchematicGeometry,
} from "./api";
import { SchematicSetup } from "./SchematicSetup";
import { missingSchematicLineIDs } from "./schematicGeometry";
import { movementKey, movementKind, movementPath, reconcileMovements, type GeoCoordinate } from "./trafficGeometry";

const tileURL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const lineSlots = Array.from({ length: 16 }, (_, index) => `line_${String(index).padStart(2, "0")}`);
const maximumWaypoints = 8;

type LineDrawingState = { slotID: string; start?: GeoCoordinate; replace: boolean };
type PathDrawingState = {
  entryLineID: string;
  exitLineID: string;
  waypoints: GeoCoordinate[];
  adding: boolean;
  selectedWaypoint: number | null;
};

function cloneLines(lines: MapLineData[]) {
  return lines.map((line) => ({ ...line, geometry: { ...line.geometry, coordinates: line.geometry.coordinates.map((point) => [...point] as GeoCoordinate) } }));
}

function lineValidationError(lines: MapLineData[]) {
  if (lines.length < 2) return "Eine Messstelle benötigt mindestens zwei Linien.";
  if (lines.length > 16) return "Pro Messstelle sind höchstens 16 Linien möglich.";
  if (new Set(lines.map((line) => line.id)).size !== lines.length) return "Jeder StreetEye-Slot darf nur einmal vergeben werden.";
  if (lines.some((line) => !lineSlots.includes(line.id))) return "Es sind nur die StreetEye-Slots 00 bis 15 zulässig.";
  if (lines.some((line) => !line.name.trim())) return "Jede Linie benötigt einen Anzeigenamen.";
  if (lines.some((line) => line.geometry.type !== "LineString" || line.geometry.coordinates.length < 2)) return "Jede Linie benötigt mindestens zwei Kartenpunkte.";
  if (lines.some((line) => new Set(line.geometry.coordinates.map((coordinate) => coordinate.join(":"))).size < 2)) return "Start- und Endpunkt einer Linie müssen verschieden sein.";
  return "";
}

function movementValidationError(movements: MapMovementData[]) {
  if (movements.some((movement) => movement.mode === "manual" && (movement.waypoints.length < 1 || movement.waypoints.length > maximumWaypoints))) {
    return `Manuelle Fahrwege benötigen einen bis ${maximumWaypoints} Kontrollpunkte.`;
  }
  return "";
}

function schematicAssignmentNotice(schematic: MapSchematicGeometry | null, lines: MapLineData[]) {
  if (!schematic) return "";
  const missingLineIDs = missingSchematicLineIDs(schematic);
  if (missingLineIDs.length === 0) return "";
  const names = new Map(lines.map((line) => [line.id, line.name]));
  return `Schematische Linienzuordnung unvollständig: ${missingLineIDs.map((lineID) => names.get(lineID) ?? lineID).join(", ")}.`;
}

function slotLabel(slotID: string) { return `Slot ${slotID.slice(-2)}`; }

function movementLabel(lines: MapLineData[], movement: MapMovementData) {
  const entry = lines.find((line) => line.id === movement.entry_line_id)?.name ?? movement.entry_line_id;
  const exit = lines.find((line) => line.id === movement.exit_line_id)?.name ?? movement.exit_line_id;
  return movement.entry_line_id === movement.exit_line_id ? `${entry} · U-Turn` : `${entry} → ${exit}`;
}

function modeLabel(mode: MapMovementData["mode"]) {
  return mode === "auto" ? "Automatisch" : mode === "manual" ? "Manuell" : "Nicht zulässig";
}

function LineEditorMap({ catalog, lines, movements, selectedLineID, selectedMovementKey, lineDrawing, pathDrawing, onSelectLine, onSelectMovement, onMapPoint, onMoveEndpoint, onMoveWaypoint, onSelectWaypoint }: {
  catalog: MapLineCatalog;
  lines: MapLineData[];
  movements: MapMovementData[];
  selectedLineID: string;
  selectedMovementKey: string;
  lineDrawing: LineDrawingState | null;
  pathDrawing: PathDrawingState | null;
  onSelectLine: (lineID: string) => void;
  onSelectMovement: (key: string) => void;
  onMapPoint: (coordinate: GeoCoordinate) => void;
  onMoveEndpoint: (lineID: string, coordinateIndex: number, coordinate: GeoCoordinate) => void;
  onMoveWaypoint: (index: number, coordinate: GeoCoordinate) => void;
  onSelectWaypoint: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const tileLayerRef = useRef<TileLayer | null>(null);
  const tileFailedRef = useRef(false);
  const movementLayerRef = useRef<LayerGroup | null>(null);
  const selectedMovementLayerRef = useRef<LayerGroup | null>(null);
  const lineLayerRef = useRef<LayerGroup | null>(null);
  const drawingLayerRef = useRef<LayerGroup | null>(null);
  const centerMarkerRef = useRef<Marker | null>(null);
  const [tileState, setTileState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView([catalog.latitude, catalog.longitude], 18);
    const tiles = L.tileLayer(tileURL, { attribution: tileAttribution, maxZoom: 20 });
    tiles.on("loading", () => { tileFailedRef.current = false; setTileState("loading"); });
    tiles.on("tileerror", () => { tileFailedRef.current = true; setTileState("error"); });
    tiles.on("load", () => { if (!tileFailedRef.current) setTileState("ready"); });
    tiles.addTo(map);
    tileLayerRef.current = tiles;
    movementLayerRef.current = L.layerGroup().addTo(map);
    selectedMovementLayerRef.current = L.layerGroup().addTo(map);
    lineLayerRef.current = L.layerGroup().addTo(map);
    drawingLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      movementLayerRef.current = null;
      selectedMovementLayerRef.current = null;
      lineLayerRef.current = null;
      drawingLayerRef.current = null;
      centerMarkerRef.current = null;
    };
  }, [catalog.latitude, catalog.longitude]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setView([catalog.latitude, catalog.longitude], 18);
    centerMarkerRef.current?.remove();
    centerMarkerRef.current = L.marker([catalog.latitude, catalog.longitude], {
      interactive: false,
      icon: L.divIcon({ className: "line-setup-center", html: "<span></span>", iconSize: [16, 16], iconAnchor: [8, 8] }),
    }).addTo(map);
  }, [catalog.latitude, catalog.longitude, catalog.measurement_point_id]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const acceptingPoint = Boolean(lineDrawing || pathDrawing?.adding);
    map.getContainer().classList.toggle("drawing", acceptingPoint);
    if (!acceptingPoint) return;
    const handleMapClick = (event: LeafletMouseEvent) => onMapPoint([event.latlng.lng, event.latlng.lat]);
    map.on("click", handleMapClick);
    return () => {
      map.off("click", handleMapClick);
      map.getContainer().classList.remove("drawing");
    };
  }, [lineDrawing, onMapPoint, pathDrawing?.adding]);

  useEffect(() => {
    const layer = movementLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const center: [number, number] = [catalog.latitude, catalog.longitude];
    const selectedMovement = movements.find((movement) => movementKey(movement.entry_line_id, movement.exit_line_id) === selectedMovementKey);
    movements.forEach((movement) => {
      const key = movementKey(movement.entry_line_id, movement.exit_line_id);
      if (key === selectedMovementKey || movement.entry_line_id !== selectedMovement?.entry_line_id) return;
      const positions = movementPath(center, lines, movement);
      if (positions.length < 2) return;
      const path = L.polyline(positions, {
        className: "line-movement-path",
        color: "#4f7d69",
        dashArray: movement.mode === "manual" ? undefined : "5 4",
        opacity: 0.18,
        weight: 2,
      }).addTo(layer);
      path.bindTooltip(`${movementLabel(lines, movement)} · ${modeLabel(movement.mode)}`, { direction: "top", sticky: true });
      path.on("click", (event) => {
        if (lineDrawing || pathDrawing?.adding) return;
        L.DomEvent.stopPropagation(event.originalEvent);
        onSelectMovement(key);
      });
    });
  }, [catalog.latitude, catalog.longitude, lineDrawing, lines, movements, onSelectMovement, pathDrawing?.adding, selectedMovementKey]);

  useEffect(() => {
    const layer = selectedMovementLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const movement = movements.find((item) => movementKey(item.entry_line_id, item.exit_line_id) === selectedMovementKey);
    if (!movement) return;
    const preview = pathDrawing && selectedMovementKey === movementKey(pathDrawing.entryLineID, pathDrawing.exitLineID)
      ? { ...movement, mode: "manual" as const, waypoints: pathDrawing.waypoints }
      : movement;
    const positions = movementPath([catalog.latitude, catalog.longitude], lines, preview);
    if (positions.length < 2) return;
    const path = L.polyline(positions, {
      className: "line-movement-path selected",
      color: "#9f0f27",
      dashArray: preview.mode === "manual" && preview.waypoints.length > 0 ? undefined : "5 4",
      opacity: 0.95,
      weight: 5,
    }).addTo(layer);
    path.bindTooltip(`${movementLabel(lines, preview)} · ${modeLabel(preview.mode)}`, { direction: "top", sticky: true });
    path.on("click", (event) => {
      if (lineDrawing || pathDrawing?.adding) return;
      L.DomEvent.stopPropagation(event.originalEvent);
      onSelectMovement(selectedMovementKey);
    });
    if (preview.mode !== "manual") return;
    preview.waypoints.forEach(([longitude, latitude], index) => {
      const selected = pathDrawing?.selectedWaypoint === index;
      const handle = L.marker([latitude, longitude], {
        draggable: true,
        keyboard: true,
        title: `Kontrollpunkt ${index + 1} verschieben`,
        icon: L.divIcon({ className: `line-path-handle${selected ? " selected" : ""}`, html: `<span>${index + 1}</span>`, iconSize: [20, 20], iconAnchor: [10, 10] }),
      }).addTo(layer);
      handle.on("mousedown", (event) => L.DomEvent.stopPropagation(event.originalEvent));
      handle.on("click", (event) => { L.DomEvent.stopPropagation(event.originalEvent); onSelectWaypoint(index); });
      handle.on("dragstart", () => onSelectWaypoint(index));
      handle.on("dragend", () => { const point = handle.getLatLng(); onMoveWaypoint(index, [point.lng, point.lat]); });
    });
  }, [catalog.latitude, catalog.longitude, lineDrawing, lines, movements, onMoveWaypoint, onSelectMovement, onSelectWaypoint, pathDrawing, selectedMovementKey]);

  useEffect(() => {
    const layer = lineLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    lines.forEach((line) => {
      const positions = line.geometry.coordinates.map(([longitude, latitude]) => L.latLng(latitude, longitude));
      const selected = line.id === selectedLineID;
      const polyline = L.polyline(positions, { className: `line-setup-polyline${selected ? " selected" : ""}`, color: selected ? "#9f0f27" : "#2e6684", opacity: 0.94, weight: selected ? 6 : 4 }).addTo(layer);
      polyline.bindTooltip(`${slotLabel(line.id)} · ${line.name}`, { permanent: true, direction: "center", className: "line-setup-label" });
      polyline.on("click", (event) => {
        if (lineDrawing || pathDrawing?.adding) return;
        L.DomEvent.stopPropagation(event.originalEvent);
        onSelectLine(line.id);
      });
      if (!selected) return;
      [0, positions.length - 1].forEach((coordinateIndex, handleIndex) => {
        const handle = L.marker(positions[coordinateIndex], {
          draggable: true,
          keyboard: true,
          title: `${line.name}: Punkt ${handleIndex + 1} verschieben`,
          icon: L.divIcon({ className: "line-setup-handle", html: `<span>${handleIndex + 1}</span>`, iconSize: [22, 22], iconAnchor: [11, 11] }),
        }).addTo(layer);
        handle.on("mousedown", (event) => L.DomEvent.stopPropagation(event.originalEvent));
        handle.on("dragend", () => { const position = handle.getLatLng(); onMoveEndpoint(line.id, coordinateIndex, [position.lng, position.lat]); });
      });
    });
  }, [lineDrawing, lines, onMoveEndpoint, onSelectLine, pathDrawing?.adding, selectedLineID]);

  useEffect(() => {
    const layer = drawingLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (lineDrawing?.start) L.marker([lineDrawing.start[1], lineDrawing.start[0]], { interactive: false, icon: L.divIcon({ className: "line-setup-handle drawing", html: "<span>1</span>", iconSize: [22, 22], iconAnchor: [11, 11] }) }).addTo(layer);
  }, [lineDrawing]);

  return <div className="line-editor-shell">
    <div className="line-editor-map" ref={containerRef} aria-label="OpenStreetMap zum Einzeichnen der Messlinien und Fahrwege" />
    {tileState === "error" && <div className="line-map-tile-error" role="status"><span>Die OSM-Kartenkacheln konnten nicht vollständig geladen werden.</span><button type="button" className="text-action" onClick={() => { tileFailedRef.current = false; setTileState("loading"); tileLayerRef.current?.redraw(); }}>Erneut laden</button></div>}
  </div>;
}

export function MapLineSetup({ onSignedOut, onSaved, onMockDataRequested, simulationMeasurementPointID }: { onSignedOut: () => void; onSaved: () => void; onMockDataRequested?: (measurementPointID: string) => void; simulationMeasurementPointID: string }) {
  const [points, setPoints] = useState<Device[]>([]);
  const [pointID, setPointID] = useState("");
  const [catalog, setCatalog] = useState<MapLineCatalog | null>(null);
  const [draftLines, setDraftLines] = useState<MapLineData[]>([]);
  const [draftMovements, setDraftMovements] = useState<MapMovementData[]>([]);
  const [draftSchematic, setDraftSchematic] = useState<MapSchematicGeometry | null>(null);
  const [draftLatitude, setDraftLatitude] = useState(0);
  const [draftLongitude, setDraftLongitude] = useState(0);
  const [selectedLineID, setSelectedLineID] = useState("");
  const [selectedMovementKey, setSelectedMovementKey] = useState("");
  const [lineDrawing, setLineDrawing] = useState<LineDrawingState | null>(null);
  const [pathDrawing, setPathDrawing] = useState<PathDrawingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const handleError = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof APIError && reason.status === 401) { onSignedOut(); return; }
    setError(fallback);
  }, [onSignedOut]);

  useEffect(() => {
    let active = true;
    getDevices().then((result) => { const simulationPoints = result.filter((point) => point.id === simulationMeasurementPointID); if (active) { setPoints(simulationPoints); setPointID(simulationPoints[0]?.id || ""); } })
      .catch((reason: unknown) => { if (active) handleError(reason, "Die Messstellen konnten nicht geladen werden."); });
    return () => { active = false; };
  }, [handleError, simulationMeasurementPointID]);

  useEffect(() => {
    if (!pointID) return;
    let active = true;
    getMapLineCatalog(pointID).then((result) => {
      if (!active) return;
      const lines = cloneLines(result.lines);
      const movements = reconcileMovements(lines, result.movements);
      setCatalog(result); setDraftLatitude(result.latitude); setDraftLongitude(result.longitude); setDraftLines(lines); setDraftMovements(movements);
      setDraftSchematic(result.schematic ? structuredClone(result.schematic) : null);
      setSelectedLineID(lines[0]?.id ?? "");
      setSelectedMovementKey(movements[0] ? movementKey(movements[0].entry_line_id, movements[0].exit_line_id) : "");
    }).catch((reason: unknown) => { if (active) handleError(reason, "Der Linienkatalog konnte nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [handleError, pointID]);

  const selectedLine = draftLines.find((line) => line.id === selectedLineID);
  const selectedMovement = draftMovements.find((movement) => movementKey(movement.entry_line_id, movement.exit_line_id) === selectedMovementKey);
  const validationError = lineValidationError(draftLines) || movementValidationError(draftMovements);
  const schematicNotice = schematicAssignmentNotice(draftSchematic, draftLines);
  const dirty = catalog !== null && (draftLatitude !== catalog.latitude || draftLongitude !== catalog.longitude || JSON.stringify(draftLines) !== JSON.stringify(catalog.lines) || JSON.stringify(draftMovements) !== JSON.stringify(reconcileMovements(catalog.lines, catalog.movements)) || JSON.stringify(draftSchematic) !== JSON.stringify(catalog.schematic ?? null));
  const usedSlots = useMemo(() => new Set(draftLines.map((line) => line.id)), [draftLines]);

  const replaceLines = useCallback((nextLines: MapLineData[], movements = draftMovements) => {
    const reconciledMovements = reconcileMovements(nextLines, movements);
    setDraftLines(nextLines);
    setDraftMovements(reconciledMovements);
    setDraftSchematic((schematic) => {
      if (!schematic) return null;
      const lineIDs = new Set(nextLines.map((line) => line.id));
      const existingPaths = new Map(schematic.paths.map((path) => [movementKey(path.entry_line_id, path.exit_line_id), path]));
      return {
        ...schematic,
        arms: schematic.arms.map((arm) => arm.line_id && !lineIDs.has(arm.line_id) ? { ...arm, line_id: null } : arm),
        paths: reconciledMovements.map((movement) => existingPaths.get(movementKey(movement.entry_line_id, movement.exit_line_id)) ?? {
          entry_line_id: movement.entry_line_id,
          exit_line_id: movement.exit_line_id,
          mode: movement.mode === "disabled" ? "disabled" as const : "auto" as const,
          waypoints: [],
        }),
      };
    });
  }, [draftMovements]);

  const onMapPoint = useCallback((coordinate: GeoCoordinate) => {
    if (pathDrawing?.adding) {
      setPathDrawing((current) => {
        if (!current?.adding) return current;
        if (current.waypoints.length >= maximumWaypoints) return { ...current, adding: false };
        const waypoints = [...current.waypoints, coordinate];
        return { ...current, waypoints, adding: false, selectedWaypoint: waypoints.length - 1 };
      });
      return;
    }
    if (!lineDrawing) return;
    if (!lineDrawing.start) { setLineDrawing({ ...lineDrawing, start: coordinate }); return; }
    const nextLine: MapLineData = {
      id: lineDrawing.slotID,
      name: lineDrawing.replace ? draftLines.find((line) => line.id === lineDrawing.slotID)?.name ?? slotLabel(lineDrawing.slotID) : `Linie ${lineDrawing.slotID.slice(-2)}`,
      geometry: { type: "LineString", coordinates: [lineDrawing.start, coordinate] },
    };
    const nextLines = lineDrawing.replace ? draftLines.map((line) => line.id === lineDrawing.slotID ? nextLine : line) : [...draftLines, nextLine];
    replaceLines(nextLines); setSelectedLineID(lineDrawing.slotID); setLineDrawing(null);
  }, [draftLines, lineDrawing, pathDrawing, replaceLines]);

  const onMoveEndpoint = useCallback((lineID: string, coordinateIndex: number, coordinate: GeoCoordinate) => {
    setDraftLines((lines) => lines.map((line) => {
      if (line.id !== lineID) return line;
      const coordinates = line.geometry.coordinates.map((point) => [...point] as GeoCoordinate);
      coordinates[coordinateIndex] = coordinate;
      return { ...line, geometry: { ...line.geometry, coordinates } };
    }));
  }, []);

  const onMoveWaypoint = useCallback((index: number, coordinate: GeoCoordinate) => {
    if (!selectedMovement) return;
    if (pathDrawing && movementKey(pathDrawing.entryLineID, pathDrawing.exitLineID) === selectedMovementKey) {
      setPathDrawing((current) => current ? {
        ...current,
        selectedWaypoint: index,
        waypoints: current.waypoints.map((point, pointIndex) => pointIndex === index ? coordinate : point),
      } : current);
      return;
    }
    setDraftMovements((movements) => movements.map((movement) => movementKey(movement.entry_line_id, movement.exit_line_id) === selectedMovementKey
      ? { ...movement, waypoints: movement.waypoints.map((point, pointIndex) => pointIndex === index ? coordinate : point) }
      : movement));
  }, [pathDrawing, selectedMovement, selectedMovementKey]);

  const onSelectWaypoint = useCallback((index: number) => {
    setPathDrawing((current) => current ? { ...current, selectedWaypoint: index } : current);
  }, []);

  function selectMeasurementPoint(nextPointID: string) {
    setLoading(true); setCatalog(null); setDraftSchematic(null); setLineDrawing(null); setPathDrawing(null); setError(""); setNotice(""); setPointID(nextPointID);
  }

  function updateSelectedLine(change: Partial<MapLineData>) {
    if (!selectedLine) return;
    const nextID = change.id ?? selectedLine.id;
    const nextLines = draftLines.map((line) => line.id === selectedLine.id ? { ...line, ...change } : line);
    const nextMovements = draftMovements.map((movement) => ({
      ...movement,
      entry_line_id: movement.entry_line_id === selectedLine.id ? nextID : movement.entry_line_id,
      exit_line_id: movement.exit_line_id === selectedLine.id ? nextID : movement.exit_line_id,
    }));
    setDraftSchematic((schematic) => schematic ? {
      ...schematic,
      arms: schematic.arms.map((arm) => arm.line_id === selectedLine.id ? { ...arm, line_id: nextID } : arm),
      paths: schematic.paths.map((path) => ({
        ...path,
        entry_line_id: path.entry_line_id === selectedLine.id ? nextID : path.entry_line_id,
        exit_line_id: path.exit_line_id === selectedLine.id ? nextID : path.exit_line_id,
      })),
    } : null);
    replaceLines(nextLines, nextMovements); setSelectedLineID(nextID);
  }

  function updateMovement(mode: MapMovementData["mode"], waypoints: GeoCoordinate[] = []) {
    if (!selectedMovement) return;
    setDraftMovements((movements) => movements.map((movement) => movementKey(movement.entry_line_id, movement.exit_line_id) === selectedMovementKey ? { ...movement, mode, waypoints } : movement));
  }

  function editMovementPath(movement: MapMovementData) {
    const waypoints = movement.mode === "manual" ? movement.waypoints.map((point) => [...point] as GeoCoordinate) : [];
    setPathDrawing({
      entryLineID: movement.entry_line_id,
      exitLineID: movement.exit_line_id,
      waypoints,
      adding: waypoints.length === 0,
      selectedWaypoint: null,
    });
  }

  function removeSelectedWaypoint() {
    setPathDrawing((current) => {
      if (!current || current.selectedWaypoint === null) return current;
      const waypoints = current.waypoints.filter((_, index) => index !== current.selectedWaypoint);
      return { ...current, waypoints, selectedWaypoint: null };
    });
  }

  function removeSelectedLine() {
    if (!selectedLine) return;
    const removedLineID = selectedLine.id;
    const remaining = draftLines.filter((line) => line.id !== removedLineID);
    replaceLines(remaining);
    setDraftSchematic((schematic) => schematic ? {
      ...schematic,
      arms: schematic.arms.map((arm) => arm.line_id === removedLineID ? { ...arm, line_id: null } : arm),
      paths: schematic.paths.filter((path) => path.entry_line_id !== removedLineID && path.exit_line_id !== removedLineID),
    } : null);
    setSelectedLineID(remaining[0]?.id ?? "");
  }

  function discardChanges() {
    if (!catalog) return;
    const lines = cloneLines(catalog.lines);
    const movements = reconcileMovements(lines, catalog.movements);
    setDraftLatitude(catalog.latitude); setDraftLongitude(catalog.longitude); setDraftLines(lines); setDraftMovements(movements); setDraftSchematic(catalog.schematic ? structuredClone(catalog.schematic) : null); setSelectedLineID(lines[0]?.id ?? ""); setLineDrawing(null); setPathDrawing(null); setError(""); setNotice("");
  }

  async function saveCatalog() {
    if (!catalog || validationError) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const saved = await replaceMapLineCatalog(pointID, catalog.configuration_generation, draftLines, draftMovements, draftSchematic, draftLatitude, draftLongitude);
      const lines = cloneLines(saved.lines);
      setCatalog(saved); setDraftLatitude(saved.latitude); setDraftLongitude(saved.longitude); setDraftLines(lines); setDraftMovements(reconcileMovements(lines, saved.movements));
      setDraftSchematic(saved.schematic ? structuredClone(saved.schematic) : null);
      setNotice(`Simulationsgeneration ${saved.configuration_generation} wurde gespeichert. Vorhandene Simulationsdaten sind bis zur Neugenerierung veraltet.`);
      onSaved();
      if (saved.mock_traffic_stale && onMockDataRequested && window.confirm("Die Geometrie wurde geändert. Vorhandene Simulationsdaten sind nicht mehr gültig. Jetzt neu erstellen?")) {
        onMockDataRequested(pointID);
      }
    } catch (reason) {
      handleError(reason, reason instanceof APIError && reason.status === 409 ? "Der Linienkatalog wurde zwischenzeitlich geändert. Bitte die Messstelle neu laden." : "Der Linienkatalog konnte nicht gespeichert werden.");
    } finally { setSaving(false); }
  }

  const selectedKind = catalog && selectedMovement
    ? movementKind([catalog.latitude, catalog.longitude], draftLines.find((line) => line.id === selectedMovement.entry_line_id)!, draftLines.find((line) => line.id === selectedMovement.exit_line_id)!)
    : null;

  return <main className="management-page line-setup-page">
    <div className="dashboard-heading management-heading"><div><h1>Messlinien einrichten</h1><p><strong>SIMULATIONSDATEN</strong> · Messlinien, Standardfahrwege und die schematische Kreuzungsgeometrie der serverseitigen Sandbox konfigurieren.</p></div></div>
    <section className="line-setup-intro"><div><strong>Zwei getrennte Geometrien</strong><p>Messlinien werden geografisch gepflegt. Straßenarme, Winkel und Breiten der schematischen Kreuzung stammen ausschließlich aus OSM.</p></div><div><strong>Keine Gerätebindung</strong><p>OSM-Snapshot, Slot-Zuordnung, Pfade und generierte Daten gehören dauerhaft nur zur Simulationsmessstelle; StreetEye, RP-0002 und Entwicklungsrevisionen bleiben unberührt.</p></div></section>
    <div className="line-setup-toolbar"><label>Messstelle<select value={pointID} onChange={(event) => selectMeasurementPoint(event.target.value)}>{points.map((point) => <option key={point.id} value={point.id}>{point.location}</option>)}</select></label>{catalog && <><label>Breite<input aria-label="Breitengrad der Simulationsmessstelle" type="number" min="-90" max="90" step="0.000001" value={draftLatitude} onChange={(event) => setDraftLatitude(Number(event.target.value))} /></label><label>Länge<input aria-label="Längengrad der Simulationsmessstelle" type="number" min="-180" max="180" step="0.000001" value={draftLongitude} onChange={(event) => setDraftLongitude(Number(event.target.value))} /></label><div className="line-setup-generation"><span>Konfigurationsgeneration <strong>{catalog.configuration_generation}</strong></span><span>{catalog.revision_count} gespeicherte {catalog.revision_count === 1 ? "Generation" : "Generationen"}</span><b className={`line-sync-state ${catalog.synchronization_state}`}>Sandbox-Simulation · keine StreetEye-Synchronisierung</b></div></>}</div>
    {error && <p className="page-error" role="alert">{error}</p>}{notice && <p className="page-notice" role="status">{notice}</p>}
    {loading ? <p className="subtle-loading">Linienkatalog wird geladen …</p> : catalog && <section className="line-setup-workspace">
      <aside className="line-setup-list"><div><strong>Linien</strong><span>{draftLines.length} von 16 Slots</span></div>{draftLines.map((line) => <button type="button" key={line.id} className={line.id === selectedLineID ? "active" : ""} onClick={() => { setSelectedLineID(line.id); setLineDrawing(null); setPathDrawing(null); }}><span>{slotLabel(line.id)}</span><strong>{line.name}</strong></button>)}<button type="button" className="secondary-button line-add-button" disabled={draftLines.length >= 16 || Boolean(lineDrawing || pathDrawing)} onClick={() => { const slotID = lineSlots.find((slot) => !usedSlots.has(slot)); if (slotID) { setSelectedLineID(""); setLineDrawing({ slotID, replace: false }); } }}>Neue Linie zeichnen</button></aside>
      <div className="line-setup-stage"><div className="line-drawing-status" aria-live="polite">{lineDrawing ? <><strong>{lineDrawing.start ? "Endpunkt setzen" : "Startpunkt setzen"}</strong><span>Klicke direkt in die Karte, um {slotLabel(lineDrawing.slotID)} zu zeichnen.</span><button type="button" className="text-action" onClick={() => setLineDrawing(null)}>Abbrechen</button></> : pathDrawing ? <><strong>Manuellen Fahrweg bearbeiten</strong><span>{pathDrawing.waypoints.length} von {maximumWaypoints} Kontrollpunkten. {pathDrawing.adding ? "Klicke einmal in die Karte, um einen Punkt hinzuzufügen." : "Punkte lassen sich auswählen und verschieben."}</span><span className="path-drawing-actions"><button type="button" className="text-action" disabled={!pathDrawing.adding && pathDrawing.waypoints.length >= maximumWaypoints} onClick={() => setPathDrawing({ ...pathDrawing, adding: !pathDrawing.adding, selectedWaypoint: null })}>{pathDrawing.adding ? "Hinzufügen abbrechen" : "Punkt hinzufügen"}</button><button type="button" className="text-action" disabled={pathDrawing.selectedWaypoint === null} onClick={removeSelectedWaypoint}>Ausgewählten löschen</button><button type="button" className="text-action" disabled={!pathDrawing.waypoints.length} onClick={() => setPathDrawing({ ...pathDrawing, waypoints: pathDrawing.waypoints.slice(0, -1), selectedWaypoint: null, adding: false })}>Letzten entfernen</button><button type="button" className="text-action" disabled={!pathDrawing.waypoints.length} onClick={() => { updateMovement("manual", pathDrawing.waypoints); setPathDrawing(null); }}>Pfad übernehmen</button><button type="button" className="text-action" onClick={() => setPathDrawing(null)}>Abbrechen</button></span></> : <><strong>Linien und Fahrwege</strong><span>Ausgewählte Fahrwege und weitere Ausfahrten derselben Zufahrt werden angezeigt.</span></>}</div>
        <LineEditorMap catalog={catalog} lines={draftLines} movements={draftMovements} selectedLineID={selectedLineID} selectedMovementKey={selectedMovementKey} lineDrawing={lineDrawing} pathDrawing={pathDrawing} onSelectLine={(id) => { setSelectedLineID(id); setLineDrawing(null); setPathDrawing(null); }} onSelectMovement={(key) => { setSelectedMovementKey(key); setSelectedLineID(""); setPathDrawing(null); }} onMapPoint={onMapPoint} onMoveEndpoint={onMoveEndpoint} onMoveWaypoint={onMoveWaypoint} onSelectWaypoint={onSelectWaypoint} />
        <p className="line-map-note">Gestrichelt: automatisch berechnet · durchgezogen: manuell · blass: weitere Ausfahrten derselben Zufahrt · Linienanker: rechte Hälfte in Bewegungsrichtung.</p></div>
      <aside className="line-setup-properties">{selectedLine ? <><div><span>Ausgewählte Linie</span><h2>{selectedLine.name}</h2></div><label>StreetEye-Slot<select value={selectedLine.id} onChange={(event) => updateSelectedLine({ id: event.target.value })}>{lineSlots.map((slot) => <option key={slot} value={slot} disabled={slot !== selectedLine.id && usedSlots.has(slot)}>{slotLabel(slot)} · {slot}</option>)}</select></label><label>Anzeigename<input value={selectedLine.name} maxLength={80} onChange={(event) => updateSelectedLine({ name: event.target.value })} /></label><dl className="line-coordinate-readout">{selectedLine.geometry.coordinates.map(([longitude, latitude], index) => <div key={index}><dt>Punkt {index + 1}</dt><dd>{latitude.toFixed(6)}, {longitude.toFixed(6)}</dd></div>)}</dl><button type="button" className="secondary-button" onClick={() => setLineDrawing({ slotID: selectedLine.id, replace: true })}>Linie neu zeichnen</button><button type="button" className="danger-link" onClick={removeSelectedLine}>Linie löschen</button></> : selectedMovement ? <><div><span>Ausgewählter Fahrweg</span><h2>{movementLabel(draftLines, selectedMovement)}</h2></div><p className="movement-kind">Default: {selectedKind === "straight" ? "gerade" : selectedKind === "uturn" ? "U-Turn" : "Kreisbogen"}</p><label>Darstellung<select aria-label="Pfadmodus" value={pathDrawing && movementKey(pathDrawing.entryLineID, pathDrawing.exitLineID) === selectedMovementKey ? "manual" : selectedMovement.mode} onChange={(event) => { const mode = event.target.value as MapMovementData["mode"]; setPathDrawing(null); if (mode === "manual") editMovementPath(selectedMovement); else updateMovement(mode); }}><option value="auto">Automatisch</option><option value="manual">Manuell</option><option value="disabled">Nicht zulässig</option></select></label><button type="button" className="secondary-button" disabled={selectedMovement.mode === "disabled"} onClick={() => editMovementPath(selectedMovement)}>{selectedMovement.mode === "manual" ? "Manuellen Pfad bearbeiten" : "Manuellen Pfad zeichnen"}</button>{selectedMovement.mode === "manual" && <button type="button" className="text-action" onClick={() => { setPathDrawing(null); updateMovement("auto"); }}>Auf Default zurücksetzen</button>}<small>{pathDrawing && movementKey(pathDrawing.entryLineID, pathDrawing.exitLineID) === selectedMovementKey ? `${pathDrawing.waypoints.length} von ${maximumWaypoints} Kontrollpunkten im Entwurf` : `${selectedMovement.waypoints.length} manuelle Kontrollpunkte`}</small></> : <div className="line-properties-empty"><strong>Linie oder Fahrweg wählen</strong><p>Wähle links eine Linie oder unten eine gerichtete Bewegung.</p></div>}</aside>

      <section className="movement-setup"><div className="movement-setup-heading"><div><h2>Gerichtete Paare</h2><p>Eintritt zeilenweise, Austritt spaltenweise. Die Auswahl gilt auch für den schematischen Pfadeditor.</p></div><span>{draftMovements.filter((movement) => movement.mode !== "disabled").length} zulässig</span></div><div className="movement-matrix-wrap"><table className="movement-matrix"><thead><tr><th>Eintritt ↓ / Austritt →</th>{draftLines.map((line) => <th key={line.id}>{slotLabel(line.id)}</th>)}</tr></thead><tbody>{draftLines.map((entry) => <tr key={entry.id}><th>{slotLabel(entry.id)}<small>{entry.name}</small></th>{draftLines.map((exit) => { const key = movementKey(entry.id, exit.id); const movement = draftMovements.find((item) => movementKey(item.entry_line_id, item.exit_line_id) === key)!; return <td key={exit.id}><button type="button" className={`${movement.mode}${key === selectedMovementKey ? " selected" : ""}`} aria-label={`${entry.name} nach ${exit.name}: ${modeLabel(movement.mode)}`} onClick={() => { setSelectedMovementKey(key); setSelectedLineID(""); setLineDrawing(null); setPathDrawing(null); }}>{movement.mode === "disabled" ? "–" : movement.mode === "manual" ? "M" : entry.id === exit.id ? "U" : "A"}</button></td>; })}</tr>)}</tbody></table></div></section>

      <SchematicSetup measurementPointID={pointID} configurationGeneration={catalog.configuration_generation} lines={draftLines} geometry={draftSchematic} selectedPathKey={selectedMovementKey} onSelectPath={(key) => { setSelectedMovementKey(key); setSelectedLineID(""); setLineDrawing(null); setPathDrawing(null); }} onChange={setDraftSchematic} />

      <footer className="line-setup-actions"><div>{validationError ? <span className="line-validation-error">{validationError}</span> : schematicNotice ? <span className="schematic-demo-note">{schematicNotice} Die Geometrie wird als Simulationsentwurf gespeichert; das Dashboard zeigt bis zur Zuordnung weiterhin keinen Fluss.</span> : <span>{draftLines.length} gültige Linien und {draftMovements.length} gerichtete Paare.</span>}</div><button type="button" className="secondary-button" disabled={!dirty || saving} onClick={discardChanges}>Änderungen verwerfen</button><button type="button" className="primary-button" disabled={!dirty || Boolean(validationError) || saving || Boolean(lineDrawing || pathDrawing)} onClick={() => void saveCatalog()}>{saving ? "Speichert …" : "Simulationsgeneration speichern"}</button></footer>
    </section>}
  </main>;
}
